import { View, Text, Alert, Image } from 'react-native';
import { useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter } from 'expo-router';
import { trpc } from '../../lib/trpc';
import { trackOnboardingStepCompleted } from '../../lib/product-events';
import {
  AccountTaxSubtypeReviewSheet,
  AccountConnectionActions,
  AddManualAccountSheet,
  OnboardingShell,
  Button,
  Card,
  ConnectionAccountRows,
  MortgagePlanningReviewSheet,
  mortgageNeedsPlanningReview,
  Badge,
  SnapTradePortal,
  useConfirmationDialog,
  WebHead,
} from '../../components';
import { usePlaidConnection } from '../../hooks/usePlaidConnection';
import { useSnapTradeConnection } from '../../hooks/useSnapTradeConnection';

function isWaitingForInitialSync(connection: {
  provider: string;
  status: string;
  lastSyncedAt: string | null;
}) {
  if (connection.provider === 'manual') return false;
  if (connection.status === 'syncing') return true;
  return connection.status === 'active' && connection.lastSyncedAt == null;
}

export default function Step2Connect() {
  const router = useRouter();
  const pathname = usePathname();
  const { confirm } = useConfirmationDialog();
  const [pendingReviewConnectionId, setPendingReviewConnectionId] = useState<string | null>(null);
  const [reviewSheetAccountIds, setReviewSheetAccountIds] = useState<string[]>([]);
  const [mortgageReviewLiabilityIds, setMortgageReviewLiabilityIds] = useState<string[]>([]);
  const [manualSheetOpen, setManualSheetOpen] = useState(false);
  const [manualSheetConnectionId, setManualSheetConnectionId] = useState<string | null>(null);
  const mortgageReviewDismissedRef = useRef(false);

  const connectionsQ = trpc.connections.list.useQuery(undefined, {
    refetchInterval: (query) => {
      const isSyncing = query.state.data?.some(isWaitingForInitialSync);
      return isSyncing ? 2000 : false;
    },
  });
  const connections = connectionsQ.data ?? [];
  const hasConnected = connections.length > 0;
  const isWaitingForSync = connections.some(isWaitingForInitialSync);
  const pendingReviewAccountsQ = trpc.accounts.list.useQuery(
    pendingReviewConnectionId
      ? { connectionId: pendingReviewConnectionId, needsTaxSubtypeReview: true }
      : { needsTaxSubtypeReview: true },
    {
      enabled: !!pendingReviewConnectionId,
      refetchInterval: (query) => {
        if (!pendingReviewConnectionId) return false;
        const connection = connectionsQ.data?.find((item) => item.id === pendingReviewConnectionId);
        if ((query.state.data?.length ?? 0) > 0) return false;
        return connection?.status === 'syncing' ? 2000 : false;
      },
    },
  );
  const accountsQ = trpc.accounts.list.useQuery(undefined, {
    enabled: hasConnected,
    refetchInterval: () => (isWaitingForSync ? 2000 : false),
  });
  const liabilitiesQ = trpc.liabilities.list.useQuery({ type: 'mortgage' }, {
    enabled: hasConnected,
    refetchInterval: () => (isWaitingForSync ? 2000 : false),
  });
  const advanceMutation = trpc.households.updateOnboardingStep.useMutation();

  const bankConnectionCount = connections.filter(
    (connection) => connection.provider === 'plaid' || connection.provider === 'teller',
  ).length;
  const brokerageConnectionCount = connections.filter((connection) => connection.provider === 'snaptrade').length;
  const hasTransactionReviewSource = bankConnectionCount > 0;
  const accounts = accountsQ.data ?? [];
  const liabilities = liabilitiesQ.data ?? [];
  const accountsByConnection = useMemo(() => {
    const next = new Map<string, typeof accounts>();
    accounts.forEach((account) => {
      if (!account.connectionId) return;
      const rows = next.get(account.connectionId) ?? [];
      rows.push(account);
      next.set(account.connectionId, rows);
    });
    return next;
  }, [accounts]);
  const liabilitiesByAccountId = useMemo(
    () => new Map(liabilities.map((liability) => [liability.accountId, liability] as const)),
    [liabilities],
  );
  const mortgageLiabilitiesNeedingReview = useMemo(
    () => liabilities.filter(mortgageNeedsPlanningReview),
    [liabilities],
  );

  const plaid = usePlaidConnection({
    onSuccess: async (connectionId) => {
      setPendingReviewConnectionId(connectionId);
      await Promise.allSettled([
        connectionsQ.refetch(),
        accountsQ.refetch(),
        liabilitiesQ.refetch(),
      ]);
    },
    onError: (err) => Alert.alert('Connection Error', err),
  });

  const snaptrade = useSnapTradeConnection({
    onSuccess: async (connectionId) => {
      setPendingReviewConnectionId(connectionId);
      await Promise.allSettled([
        connectionsQ.refetch(),
        accountsQ.refetch(),
        liabilitiesQ.refetch(),
      ]);
    },
    onError: (err) => Alert.alert('Connection Error', err),
  });

  useEffect(() => {
    if (!pendingReviewConnectionId || reviewSheetAccountIds.length > 0) return;

    const connection = connectionsQ.data?.find((item) => item.id === pendingReviewConnectionId);
    const pendingAccounts = pendingReviewAccountsQ.data ?? [];

    if (pendingAccounts.length > 0) {
      setReviewSheetAccountIds(pendingAccounts.map((account) => account.id));
      return;
    }

    if (connection && connection.status !== 'syncing') {
      setPendingReviewConnectionId(null);
    }
  }, [
    connectionsQ.data,
    pendingReviewAccountsQ.data,
    pendingReviewConnectionId,
    reviewSheetAccountIds.length,
  ]);

  useEffect(() => {
    if (mortgageReviewDismissedRef.current) return;
    if (isWaitingForSync || reviewSheetAccountIds.length > 0) return;
    if (mortgageReviewLiabilityIds.length > 0) return;
    if (mortgageLiabilitiesNeedingReview.length === 0) return;

    setMortgageReviewLiabilityIds(
      mortgageLiabilitiesNeedingReview.map((liability) => liability.id),
    );
  }, [
    isWaitingForSync,
    mortgageLiabilitiesNeedingReview,
    mortgageReviewLiabilityIds.length,
    reviewSheetAccountIds.length,
  ]);

  const reviewAccountSource = [
    ...(pendingReviewAccountsQ.data ?? []),
    ...accounts,
  ].filter(
    (account, index, all) => all.findIndex((candidate) => candidate.id === account.id) === index,
  );
  const reviewSheetAccounts = reviewAccountSource.filter((account) =>
    reviewSheetAccountIds.includes(account.id),
  );
  const mortgageReviewLiabilities = liabilities.filter((liability) =>
    mortgageReviewLiabilityIds.includes(liability.id),
  );
  const hasPendingAccountReview =
    reviewSheetAccountIds.length > 0 ||
    (pendingReviewAccountsQ.data?.length ?? 0) > 0;

  const closeTaxSubtypeReview = () => {
    setReviewSheetAccountIds([]);
    setPendingReviewConnectionId(null);
  };

  const handleTaxSubtypeSaved = async () => {
    await Promise.allSettled([
      connectionsQ.refetch(),
      accountsQ.refetch(),
      liabilitiesQ.refetch(),
      pendingReviewAccountsQ.refetch(),
    ]);
    setReviewSheetAccountIds([]);
    setPendingReviewConnectionId(null);
  };

  const closeMortgageReview = () => {
    mortgageReviewDismissedRef.current = true;
    setMortgageReviewLiabilityIds([]);
  };

  const handleMortgagePlanningSaved = async (liabilityId: string) => {
    await Promise.allSettled([
      liabilitiesQ.refetch(),
      accountsQ.refetch(),
    ]);
    setMortgageReviewLiabilityIds((current) =>
      current.filter((id) => id !== liabilityId),
    );
  };

  const isLinking = plaid.isLoading || snaptrade.isLoading;
  const isLoadingConnections = connectionsQ.isLoading && !connectionsQ.data;

  const advanceToNextStep = async () => {
    const nextStepNumber = hasTransactionReviewSource ? 3 : 4;
    const nextRoute = hasTransactionReviewSource
      ? '/(onboarding)/step-3-transactions'
      : '/(onboarding)/step-4-income';

    await advanceMutation.mutateAsync({ step: nextStepNumber });
    trackOnboardingStepCompleted({
      route: pathname,
      stepNumber: 2,
      nextStepNumber,
      completionKind: hasConnected ? 'continue' : 'skip',
      metadata: {
        connectedAccountCount: connections.length,
        bankConnectionCount,
        brokerageConnectionCount,
        didSkip: !hasConnected,
        skippedTransactionReview: !hasTransactionReviewSource,
      },
    });
    router.replace(nextRoute as any);
  };

  const handleContinue = async () => {
    if (isLoadingConnections || isWaitingForSync || hasPendingAccountReview) return;

    if (!hasConnected) {
      const choice = await confirm({
        title: 'Per Diem works better with linked accounts',
        message:
          'When you link your accounts, Per Diem can use your spending history to auto-calculate a first-draft budget and help keep your spending on track as new transactions come in. You can still add accounts later.',
        actions: [
          { key: 'connect', label: 'Link accounts', role: 'cancel' },
          {
            key: 'skip',
            label: 'Skip for now',
            variant: 'ghost',
          },
        ],
      });

      if (choice !== 'skip') return;
    }

    await advanceToNextStep();
  };

  return (
    <>
      <WebHead><title>Step 2: Connect Accounts – Per Diem</title></WebHead>
      <SnapTradePortal
        loginLink={snaptrade.oauthUrl}
        isOpen={snaptrade.isOpen}
        onClose={snaptrade.close}
        onSuccess={snaptrade.handleSuccess}
        onError={(err) => Alert.alert('Connection Error', err)}
      />
      <AccountTaxSubtypeReviewSheet
        visible={reviewSheetAccounts.length > 0}
        accounts={reviewSheetAccounts}
        onClose={closeTaxSubtypeReview}
        onSaved={handleTaxSubtypeSaved}
      />
      <MortgagePlanningReviewSheet
        visible={reviewSheetAccounts.length === 0 && mortgageReviewLiabilities.length > 0}
        liabilities={mortgageReviewLiabilities}
        onClose={closeMortgageReview}
        onSaved={handleMortgagePlanningSaved}
      />
      <AddManualAccountSheet
        visible={manualSheetOpen}
        manualConnectionId={manualSheetConnectionId}
        onClose={() => {
          setManualSheetOpen(false);
          setManualSheetConnectionId(null);
        }}
        onCreated={async () => {
          await Promise.allSettled([
            connectionsQ.refetch(),
            accountsQ.refetch(),
            liabilitiesQ.refetch(),
          ]);
        }}
      />
      <OnboardingShell
        step={1}
        title="Connect Your Accounts"
      subtitle="Link your bank and brokerage accounts so Per Diem can build your complete financial picture."
      footer={
        <View className="gap-3">
          <Button
            testID="onboarding-connect-continue-button"
            accessibilityLabel={hasConnected ? 'Continue from account connection' : 'Skip account connection'}
            onPress={handleContinue}
            disabled={isLoadingConnections || isWaitingForSync || hasPendingAccountReview || (!hasConnected && isLinking)}
            loading={advanceMutation.isPending}
          >
            {isLoadingConnections
              ? 'Loading connections…'
              : isWaitingForSync
              ? 'Syncing transactions…'
              : hasPendingAccountReview
              ? 'Review account details'
              : hasConnected
              ? 'Continue'
              : 'Skip for now'}
          </Button>
          {isWaitingForSync && (
            <Text className="text-xs text-gray-400 text-center">
              We will unlock the next step once transactions finish loading.
            </Text>
          )}
          {hasPendingAccountReview && !isWaitingForSync && (
            <Text className="text-xs text-gray-400 text-center">
              Review account tax labels before moving on.
            </Text>
          )}
          {!hasConnected && (
            <Text className="text-xs text-gray-400 text-center">
              You can connect accounts later from the Accounts screen.
            </Text>
          )}
        </View>
      }
    >
      <View className="gap-4">
        {/* Connected accounts list */}
        {connections.length > 0 && (
          <Card>
            <Text className="text-sm font-semibold text-gray-700 mb-2">
              Connected Institutions ({connections.length})
            </Text>
            {connections.map((conn) => {
              const connAccounts = accountsByConnection.get(conn.id) ?? [];
              return (
                <View key={conn.id} className="py-2 gap-3 border-b border-gray-50 last:border-b-0 dark:border-gray-700">
                  <View className="flex-row items-center justify-between gap-3">
                    {conn.institutionLogoBase64 ? (
                      <Image
                        source={{ uri: `data:image/png;base64,${conn.institutionLogoBase64}` }}
                        style={{ width: 28, height: 28, borderRadius: 6 }}
                        resizeMode="contain"
                      />
                    ) : (
                      <View className="w-7 h-7 rounded-md bg-gray-100 dark:bg-gray-700 items-center justify-center">
                        <Text className="text-xs font-semibold text-gray-400">
                          {(conn.institutionName ?? '?')[0]?.toUpperCase()}
                        </Text>
                      </View>
                    )}
                    <View className="flex-1">
                      <Text className="text-sm text-gray-800 dark:text-gray-200">{conn.institutionName ?? 'Account'}</Text>
                      <Text className="text-xs text-gray-400 capitalize">
                        {conn.provider}
                        {connAccounts.length > 0
                          ? ` · ${connAccounts.length} account${connAccounts.length === 1 ? '' : 's'}`
                          : ''}
                      </Text>
                    </View>
                    <Badge
                      label={
                        conn.status === 'active'
                          ? 'Connected'
                          : conn.status === 'syncing'
                          ? 'Syncing…'
                          : conn.status
                      }
                      variant={conn.status === 'syncing' ? 'yellow' : 'green'}
                    />
                  </View>
                  <ConnectionAccountRows
                    accounts={connAccounts}
                    liabilitiesByAccountId={liabilitiesByAccountId}
                    onReviewTaxAccount={(accountId) => setReviewSheetAccountIds([accountId])}
                    onReviewMortgage={(liabilityId) => {
                      mortgageReviewDismissedRef.current = false;
                      setMortgageReviewLiabilityIds([liabilityId]);
                    }}
                    className="pl-10"
                  />
                </View>
              );
            })}
          </Card>
        )}

        {/* Connect buttons */}
        <Card>
          <AccountConnectionActions
            onConnectAccount={plaid.open}
            connectAccountLoading={plaid.isLoading}
            onConnectBrokerage={snaptrade.open}
            connectBrokerageLoading={snaptrade.isLoading}
            onAddManualAccount={() => {
              setManualSheetConnectionId(null);
              setManualSheetOpen(true);
            }}
            disabled={isLinking}
            className="mt-0"
          />
        </Card>

        <Card className="bg-blue-50">
          <Text className="text-sm font-semibold text-blue-800 mb-1">Bank-level security</Text>
          <Text className="text-xs text-blue-600">
            We use read-only access. We never see your credentials and cannot move money.
            Your data is encrypted in transit and at rest.
          </Text>
        </Card>
      </View>
      </OnboardingShell>
    </>
  );
}
